import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import useIntakeCooldown from "../../hooks/useIntakeCooldown";
import useIntakeUpload from "../../hooks/useIntakeUpload";

import { deleteInputDocument, uploadInputDocument } from "../../redux/actions/assessment";
import { flattenError } from "../../utils/errorDisplay";
import { intakeErrorText } from "../../utils/intakeLimits";

const MAX_DOCUMENTS = 3;

const Wrapper = styled.div`
  margin-top: 8px;
`;

const Hint = styled.p`
  margin: 0 0 10px;
  color: #6b7280;
  font-size: 0.85rem;
  line-height: 1.5;
`;

const PickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  label {
    display: inline-block;
    padding: 7px 14px;
    border-radius: 6px;
    border: 1px solid var(--brand-border, #cfd8dc);
    background: #fff;
    color: var(--brand-ink, #0b1c1e);
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
  }

  label[aria-disabled="true"] {
    opacity: 0.55;
    cursor: not-allowed;
  }

  input[type="file"] {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }
`;

const DocList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
`;

const DocItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid var(--brand-border, #e3ebec);
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 0.85rem;
`;

const DocMeta = styled.span`
  color: #6b7280;
  font-size: 0.78rem;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #b91c1c;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 2px 4px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin: 8px 0 0;
  color: #b91c1c;
  font-size: 0.82rem;
`;

/**
 * Upload PDFs the analysis should READ (inspections, disclosures, floor
 * plans). Only their extractable text can inform the analysis, within reading
 * limits. Distinct from the Share page's appendix, whose pages are attached.
 *
 * `onChange` exists for callers that hold their own copy of the list rather
 * than reading the listings slice — the editor renders from the report
 * document, which would otherwise still show yesterday's documents after an
 * upload.
 * @param {{listingUuid?: string, documents?: Array<Record<string, any>>, disabled?: boolean,
 *   hint?: React.ReactNode, onChange?: (documents: Array<Record<string, any>>) => void}} props
 */
const InputDocumentUploader = ({
  listingUuid,
  documents = [],
  disabled = false,
  hint,
  onChange,
}) => {
  const dispatch = useDispatch();
  const cooldown = useIntakeCooldown();
  const loading = useSelector((/** @type {any} */ state) => state.listings.documentUploadLoading);
  const uploadError = useSelector((/** @type {any} */ state) => state.listings.documentUploadError);
  const rows = Array.isArray(documents) ? documents.filter(Boolean) : [];
  const atLimit = rows.length >= MAX_DOCUMENTS;

  /** @param {Record<string, any> | undefined} listing */
  const publish = (listing) => {
    if (onChange && listing) onChange(listing.input_documents || []);
  };
  const upload = useIntakeUpload({
    targetKey: listingUuid || "",
    upload: (files) =>
      dispatch(/** @type {any} */ (uploadInputDocument(listingUuid || "", files[0]))),
    onSuccess: publish,
  });
  const busy = disabled || loading || upload.busy || !listingUuid;
  const displayedError =
    intakeErrorText(uploadError, cooldown.retryAt) ||
    flattenError(uploadError)
      .map(({ message }) => message)
      .join(" ");

  /** @param {React.ChangeEvent<HTMLInputElement>} e */
  const handleSelected = (e) => {
    const files = Array.from(e.target.files || []);
    // Upload one at a time so a rejected file names itself in the error.
    if (files.length && listingUuid && !busy && !cooldown.blocked && !atLimit) {
      void upload.start([files[0]]);
    }
    // Allow re-selecting the same file after a failure.
    e.target.value = "";
  };

  /** @param {string} documentId */
  const handleRemove = (documentId) => {
    if (!listingUuid) return;
    Promise.resolve(
      dispatch(/** @type {any} */ (deleteInputDocument(listingUuid, documentId)))
    ).then((/** @type {any} */ result) => {
      if (result?.ok) publish(result.data);
    });
  };

  return (
    <Wrapper data-testid="input-document-uploader">
      <Hint>
        {hint ||
          "Have an inspection, disclosure, floor plan, or HOA packet? Upload a text-based PDF to use its available text in your listing analysis."}{" "}
        PDFs up to 20 MB, {MAX_DOCUMENTS} max. We examine up to 50 pages per document and include up
        to 12,000 characters across all documents. Scanned pages and diagrams may not be read.
      </Hint>
      <PickerRow>
        <label htmlFor="input-document-file" aria-disabled={busy || atLimit || cooldown.blocked}>
          {loading ? "Uploading…" : "Add a document"}
        </label>
        <input
          id="input-document-file"
          type="file"
          accept="application/pdf"
          disabled={busy || atLimit || cooldown.blocked}
          onChange={handleSelected}
        />
        {atLimit && <DocMeta>Limit reached — remove one to add another.</DocMeta>}
      </PickerRow>
      {rows.length > 0 && (
        <DocList>
          {rows.map((doc) => (
            <DocItem key={doc.id || doc.filename}>
              <span>
                {doc.filename}{" "}
                <DocMeta>
                  {doc.page_count} page{doc.page_count === 1 ? "" : "s"}
                </DocMeta>
              </span>
              <RemoveButton
                type="button"
                disabled={busy}
                onClick={() => handleRemove(doc.id)}
                aria-label={`Remove ${doc.filename}`}
              >
                Remove
              </RemoveButton>
            </DocItem>
          ))}
        </DocList>
      )}
      {displayedError && <ErrorText role="alert">{displayedError}</ErrorText>}
      {upload.files.length > 0 && (
        <div>
          <p>
            {upload.files[0].name} is kept on this page for retry. Leaving or refreshing requires
            selecting it again.
          </p>
          <button
            type="button"
            disabled={busy || atLimit || cooldown.blocked}
            onClick={() => void upload.retry()}
          >
            Retry selected document
          </button>
          <button type="button" disabled={busy} onClick={upload.discard}>
            Discard selection
          </button>
        </div>
      )}
    </Wrapper>
  );
};

export default InputDocumentUploader;
